import { format } from "date-fns/esm";
import Tooltip from "../../../../../utils/element-generator/Tooltip";
import { useEffect, useState } from "react";
import { Image } from "primereact/image";
import { Skeleton } from "primereact/skeleton";

import { AnimatePresence, motion } from "framer-motion";
import useIsMobile from "../../../../../hooks/useIsMobile";

const OwnerDashboardEmployeeBCDetail = (props) => {
  const { filterValue, data, loading } = props;
  const [bc, setBC] = useState([]);
  const [viewType, setViewType] = useState("card");
  const viewOptions = [
    { label: "Card View", value: "card" },
    { label: "Table View", value: "table" },
  ];
  const isMobile = useIsMobile(950);
  const [globalConversion, setGlobalConversion] = useState(0);

  useEffect(() => {
    if (isMobile) {
      setViewType("card");
    } else setViewType("table");
  }, [isMobile]);

  const getGlobalConversion = (list) => {
    const totalNewCustomer = list.reduce((sum, bc) => sum + bc.newCustomer, 0);
    const totalNewPayingCustomer = list.reduce(
      (sum, bc) => sum + bc.newPayingCustomer,
      0
    );

    if (totalNewCustomer === 0) return 0;

    setGlobalConversion(
      ((totalNewPayingCustomer / totalNewCustomer) * 100).toFixed(2)
    );
  };

  const sumAll = (list, field) => {
    const total = list.reduce((sum, bc) => sum + bc[field], 0);
    return total;
  };

  const getGlobalConvRate = (employee) => {
    if (employee.newCustomer === 0) return "0.00";
    return (
      (employee.newCustomer / sumAll(bc, "newCustomer")) *
      employee.conversionRate
    ).toFixed(2);
  };

  useEffect(() => {
    if (data) {
      const bcDetails = data.bc_details
        .map((bc) => {
          return {
            name: bc.bc_name,
            image: bc.bc_image,
            totalAppointments: bc.total_appointment,
            returningCustomer: bc.returning_customer,
            newCustomer: bc.new_customer,
            newPayingCustomer: bc.new_paying_customer,
            conversionRate: bc.conversion_rate,
            totalSpendingNew: bc.total_spending_new_customer,
            avgSpendingPerNew: bc.average_spending_per_new_customer,
            totalSpendingReturning: bc.total_spending_returning_customer,
            totalSales: bc.total_sales,
          };
        })
        .sort((a, b) => b.totalSales - a.totalSales);

      setBC(bcDetails);
      getGlobalConversion(bcDetails);
    }
  }, [data]);

  const EmployeeSalesSkeleton = ({ rows = 5 }) => {
    const skeletonRows = Array.from({ length: rows });

    return (
      <div className="elevated-card-tight-vertical w-full p-4 border border-gray-300 rounded-xl shadow-md">
        <div className="flex flex-col items-start gap-1 mb-4">
          <label className="flex items-center gap-2 text-xl font-semibold text-black">
            <Skeleton width="8rem" height="1.5rem" />
          </label>
          <Skeleton width="12rem" height="1rem" className="rounded-md" />
        </div>

        <div className="relative overflow-x-auto rounded-xl border border-gray-300">
          <table className="min-w-[1200px] w-full table-auto text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-300 text-xs text-slate-700 uppercase tracking-wide">
                <th className="sticky left-0 bg-gray-100 z-20 px-6 py-3 text-left font-bold border-r border-gray-300">
                  Employee
                </th>
                {[
                  "Total Appointments",
                  "Returning Cust",
                  "New Cust",
                  "New Paying Cust",
                  "Conversion Rate",
                  "Conv All",
                  "Total Spending New Cust",
                  "Avg Spending Per New Cust",
                  "Total Spending Returning Cust",
                  "TOTAL SALES",
                ].map((label, i) => (
                  <th key={i} className="px-4 py-3 text-center font-semibold">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {skeletonRows.map((_, idx) => (
                <tr
                  key={idx}
                  className={`${
                    idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                  } hover:bg-gray-100 transition`}
                >
                  {/* Sticky Employee Cell */}
                  <td
                    className={`sticky left-0 ${
                      idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                    } z-10 px-6 py-4 whitespace-nowrap border-r border-gray-200 shadow-md`}
                  >
                    <div className="flex items-center justify-center gap-2 w-[12rem]">
                      <Skeleton shape="circle" width="48px" height="48px" />
                      <Skeleton
                        width="8rem"
                        height="1.25rem"
                        className="rounded"
                      />
                    </div>
                  </td>

                  {/* Fake Cells */}
                  {Array.from({ length: 10 }).map((_, i) => (
                    <td key={i} className="px-4 py-4 text-center">
                      <Skeleton
                        height="1.5rem"
                        width={i === 8 ? "6rem" : "4rem"}
                        className="mx-auto rounded"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const EmployeeCardSkeleton = () => {
    return (
      <div className="elevated-card-tight-vertical w-full p-4 border border-gray-300 rounded-xl shadow-md">
        <div className="flex flex-col items-start gap-1 mb-4">
          <label className="flex items-center gap-2 text-xl font-semibold text-black">
            <Skeleton width="8rem" height="1.5rem" />
          </label>
          <Skeleton width="12rem" height="1rem" className="rounded-md" />
          <Skeleton width="100%" height="2rem" className="rounded-md" />
        </div>
        {isMobile
          ? Array.from({ length: 3 }).map((_, idx) => (
              <EmployeeCardSkeletonMobile key={idx} />
            ))
          : Array.from({ length: 3 }).map((_, idx) => (
              <EmployeeCardSkeletonDesktop />
            ))}
      </div>
    );
  };

  const EmployeeCardSkeletonMobile = () => (
    <div className="bg-white rounded-xl w-full shadow-sm border border-gray-300 p-4 mb-4">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-4">
        <Skeleton shape="circle" size="48px" className="flex-shrink-0" />
        <div className="flex-1 space-y-1">
          <Skeleton width="60%" height="1rem" />
          <Skeleton width="40%" height="0.75rem" />
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-rows-3 gap-3 mb-4">
        {[...Array(3)].map((_, idx) => (
          <div key={idx} className="p-3 border rounded-lg space-y-2">
            <Skeleton width="20%" height="2rem" className="mx-auto" />
            <Skeleton width="30%" height="1rem" className="mx-auto" />
          </div>
        ))}
      </div>

      {/* Detailed Metrics */}
      <div className="space-y-2 text-sm">
        {[...Array(3)].map((_, idx) => (
          <div key={idx} className="flex justify-between items-center">
            <Skeleton width="30%" height="1rem" />
            <Skeleton width="10%" height="1rem" />
          </div>
        ))}
        {[...Array(3)].map((_, idx) => (
          <div key={idx} className="flex justify-between items-center">
            <Skeleton width="30%" height="1rem" />
            <Skeleton width="10%" height="1rem" />
          </div>
        ))}
      </div>
    </div>
  );

  const EmployeeCardSkeletonDesktop = () => (
    <div className="bg-white rounded-xl w-full shadow-sm border border-gray-300 p-4 mb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Skeleton shape="circle" size="48px" className="flex-shrink-0" />
          <div className="space-y-1">
            <Skeleton width="100px" height="1rem" />
            <Skeleton width="80px" height="0.75rem" />
          </div>
        </div>
        <div className="text-right space-y-1">
          <Skeleton width="80px" height="1.25rem" />
          <Skeleton width="60px" height="0.75rem" />
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[...Array(3)].map((_, idx) => (
          <div
            key={idx}
            className="p-3 border rounded-lg text-center space-y-2"
          >
            <Skeleton width="30%" height="2rem" className="mx-auto" />
            <Skeleton width="40%" height="1rem" className="mx-auto" />
          </div>
        ))}
      </div>

      {/* Detailed Metrics */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="space-y-2">
          {[...Array(3)].map((_, idx) => (
            <div key={idx} className="flex justify-between items-center">
              <Skeleton width="30%" height="1rem" />
              <Skeleton width="10%" height="1rem" />
            </div>
          ))}
        </div>
        <div className="space-y-2">
          {[...Array(3)].map((_, idx) => (
            <div key={idx} className="flex justify-between items-center">
              <Skeleton width="30%" height="1rem" />
              <Skeleton width="10%" height="1rem" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const EmployeeCard = ({ employee }) =>
    isMobile ? (
      <EmployeeCardMobile employee={employee} />
    ) : (
      <EmployeeCardDesktop employee={employee} />
    );

  const EmployeeCardMobile = ({ employee }) => (
    <div className="bg-white rounded-xl w-full shadow-sm border border-gray-300 p-4 mb-4 hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="flex flex-row items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Image
              preview
              src={employee.image}
              alt={employee.name}
              onError={(e) => {
                e.target.src = "/logo192.png";
              }}
              pt={{
                image: {
                  className:
                    "w-12 h-12 rounded-full object-cover border-2 border-gray-200 flex-shrink-0",
                },
              }}
            />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-lg">{employee.name}</h3>
            <p className="text-sm text-gray-500">Beauty Consultant</p>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-rows-3 gap-3 mb-4">
        <div className=" col-center-center text-center bg-blue-50 border border-blue-500 rounded-lg p-3">
          <div className="text-xl font-bold text-blue-600">
            {employee.totalAppointments}
          </div>
          <div className="text-xs text-gray-600">Appointments</div>
        </div>

        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 text-center bg-purple-50 border border-purple-500 rounded-lg p-3">
          <div className="col-center-center">
            <div
              className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-bold border ${getConversionColor(
                employee.conversionRate
              )}`}
            >
              <span className="mr-1">
                {getConversionIcon(employee.conversionRate)}
              </span>
              {employee.conversionRate}%
            </div>
            <div className="text-xs text-gray-600 mt-1 row-center-center gap-1">
              <Tooltip text="(New Paying Cust / New Cust) x 100%" />
              Conversion Rate
            </div>
          </div>
          <div className="col-center-center">
            <div
              className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-bold`}
            >
              {getGlobalConvRate(employee)}%
            </div>
            <div className="text-xs text-gray-600 mt-1 row-center-center gap-1">
              <Tooltip text="(Ttl New Paying Cust / Ttl New Cust) x 100%" />
              Conv All {globalConversion}%
            </div>
          </div>
        </div>
        <div className="col-center-center text-center bg-green-50 border border-green-500 rounded-lg p-3">
          <div className="text-lg font-bold text-green-600">
            {formatCurrency(employee.avgSpendingPerNew)}
          </div>
          <div className="text-xs text-gray-600">Avg/New</div>
        </div>
      </div>

      {/* Metrics */}
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Returning Cust:</span>
          <span className="font-semibold text-slate-700">
            {employee.returningCustomer} pax
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">New Cust:</span>
          <span className="font-semibold text-slate-700">
            {employee.newCustomer} pax
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">New Paying Cust:</span>
          <span className="font-semibold text-slate-700">
            {employee.newPayingCustomer} pax
          </span>
        </div>

        <div className="flex flex-wrap justify-between gap-1">
          <span className="text-gray-600 min-w-[60%]">
            Total New Cust Spending:
          </span>
          <span className="font-semibold text-slate-700 text-right break-words">
            {formatCurrency(employee.totalSpendingNew)}
          </span>
        </div>
        <div className="flex flex-wrap justify-between gap-1">
          <span className="text-gray-600 min-w-[60%]">
            Total Return Cust Spending:
          </span>
          <span className="font-semibold text-slate-700 text-right break-words">
            {formatCurrency(employee.totalSpendingReturning)}
          </span>
        </div>
        <div className="flex flex-wrap justify-between gap-1">
          <span className="text-gray-600 min-w-[60%]">Total Sales:</span>
          <span className="font-semibold text-emerald-600 text-right break-words">
            {formatCurrency(employee.totalSales)}
          </span>
        </div>
      </div>
    </div>
  );

  const EmployeeCardDesktop = ({ employee }) => (
    <div className="bg-white rounded-xl w-full shadow-sm border border-gray-300 p-4 mb-4 hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="flex flex-row items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Image
              preview
              src={employee.image}
              alt={employee.name}
              onError={(e) => {
                e.target.src = "/logo192.png";
              }}
              pt={{
                image: {
                  className:
                    "w-12 h-12 rounded-full object-cover border-2 border-gray-200 flex-shrink-0",
                },
              }}
            />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-lg">{employee.name}</h3>
            <p className="text-sm text-gray-500">Beauty Consultant</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-green-600">
            {formatCurrency(employee.totalSales)}
          </div>
          <div className="text-xs text-gray-500">Total Sales</div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid sm:grid-cols-4 gap-3 mb-4">
        <div className="col-center-center text-center bg-blue-50 border border-blue-500 rounded-lg p-3">
          <div className="text-xl font-bold text-blue-600">
            {employee.totalAppointments}
          </div>
          <div className="text-xs text-gray-600">Appointments</div>
        </div>
        <div className="col-span-2 flex flex-col sm:flex-row justify-center items-center gap-4 text-center bg-purple-50 border border-purple-500 rounded-lg p-3">
          <div className="col-center-center">
            <div
              className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-bold border ${getConversionColor(
                employee.conversionRate
              )}`}
            >
              <span className="mr-1">
                {getConversionIcon(employee.conversionRate)}
              </span>
              {employee.conversionRate}%
            </div>
            <div className="text-xs text-gray-600 mt-1 row-center-center gap-1">
              <Tooltip text="(New Paying Cust / New Cust) x 100%" />
              Conversion Rate
            </div>
          </div>
          <div className="col-center-center">
            <div
              className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-bold`}
            >
              {getGlobalConvRate(employee)}%
            </div>
            <div className="text-xs text-gray-600 mt-1 row-center-center gap-1">
              <Tooltip text="(Ttl New Paying Cust / Ttl New Cust) x 100%" />
              Conv All {globalConversion}%
            </div>
          </div>
        </div>
        <div className="col-center-center  text-center bg-green-50 border border-green-500 rounded-lg p-3">
          <div className="text-lg font-bold text-green-600 overflow-wrap break-words">
            {formatCurrency(employee.avgSpendingPerNew)}
          </div>
          <div className="text-xs text-gray-600">Avg/New</div>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Returning Cust:</span>
            <span className="font-semibold text-slate-700">
              {employee.returningCustomer} pax
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">New Cust:</span>
            <span className="font-semibold text-slate-700">
              {employee.newCustomer} pax
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">New Paying Cust:</span>
            <span className="font-semibold text-slate-700">
              {employee.newPayingCustomer} pax
            </span>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Total New Cust Spending:</span>
            <span className="font-semibold text-slate-700">
              {formatCurrency(employee.totalSpendingNew)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Total Return Cust Spending:</span>
            <span className="font-semibold text-slate-700">
              {formatCurrency(employee.totalSpendingReturning)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const formatCurrency = (value) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const formatPercentage = (value) => `${value}%`;

  const formatPax = (value) => `${value} pax`;

  const getSalesColor = (sales, target) => {
    if (sales >= target) return "bg-green-100 text-green-800 border-green-300";
    if (sales >= target - 3000000)
      return "bg-yellow-100 text-yellow-800 border-yellow-300";
    return "bg-rose-100 text-rose-800 border-rose-300";
  };

  const getConversionColor = (rate) => {
    if (rate >= 70) return "bg-green-100 text-green-800 border-green-300";
    if (rate >= 40) return "bg-yellow-100 text-yellow-800 border-yellow-300";
    return "bg-rose-100 text-rose-800 border-rose-300";
  };

  const getConversionIcon = (rate) => {
    if (rate >= 70) return "✓";
    if (rate >= 40) return "⚠";
    return "✗";
  };

  return loading ? (
    viewType === "card" ? (
      <EmployeeCardSkeleton />
    ) : (
      <EmployeeSalesSkeleton rows={3} />
    )
  ) : (
    <div className="elevated-card-tight-vertical w-full mx-auto p-4 sm:px-6 lg:px-8 border border-gray-300 rounded-xl shadow-md">
      <div className="w-full flex flex-col sm:flex-row gap-2 justify-between items-start sm:items-center sm:justify-between mb-4">
        <div className="flex flex-col items-start gap-1">
          <label className="flex items-center gap-2 text-xl font-semibold text-black">
            <i className="pi pi-user text-green-500 text-2xl"></i>
            BC Details
          </label>
          <span className="text-xs bg-gradient-to-br from-green-200 to-emerald-200 text-black font-semibold border border-green-400 rounded-md px-2 py-0.5">
            {format(new Date(filterValue?.dari_tanggal), "dd MMM yyyy")} -{" "}
            {format(new Date(filterValue?.sampai_tanggal), "dd MMM yyyy")}
          </span>
        </div>

        <div className="flex gap-2 rounded-md p-1 w-full sm:w-auto">
          {viewOptions.map((option) => (
            <button
              key={option.label}
              onClick={() => setViewType(option.value)}
              className={`px-4 py-1.5 w-full sm:w-auto rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 ease-in-out 
          ${
            viewType === option.value
              ? "bg-gradient-to-br from-green-400 to-emerald-500 text-white shadow"
              : "text-black hover:bg-green-100 bg-slate-100"
          }`}
              disabled={loading}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <AnimatePresence>
        {viewType === "card" ? (
          <div className="w-full col-start-start gap-2">
            {bc.map((employee, index) => (
              <motion.div
                key={`${employee.name} - ${index}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
                className="w-full"
              >
                <EmployeeCard employee={employee} />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="relative overflow-x-auto rounded-xl border border-gray-300">
            {/* Optional Scroll Shadows */}

            <table className="min-w-[1200px] w-full table-auto text-sm ">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-300 text-xs text-slate-700 uppercase tracking-wide">
                  <th className="sticky left-0 bg-gray-100 z-20 px-6 py-3 text-left font-bold border-r border-gray-300">
                    Employee
                  </th>
                  <th className="px-4 py-3 text-center font-semibold">
                    Total Appointments
                  </th>
                  <th className="px-4 py-3 text-center font-semibold">
                    Returning Cust
                  </th>
                  <th className="px-4 py-3 text-center font-semibold">
                    New Cust
                  </th>
                  <th className="px-4 py-3 text-center font-semibold">
                    New Paying Cust
                  </th>
                  <th className="px-4 py-3 text-center font-semibold">
                    <div className="flex flex-row items-center">
                      <Tooltip text="(New Paying Cust / New Cust) x 100%">
                        Conversion Rate
                      </Tooltip>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center font-semibold">
                    <div className="flex flex-row items-center">
                      <Tooltip text="(Ttl New Paying Cust / Ttl New Cust) x 100%">
                        Conv All {globalConversion}%
                      </Tooltip>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center font-semibold">
                    Total Spending New Cust
                  </th>
                  <th className="px-4 py-3 text-center font-semibold">
                    Avg Spending Per New Cust
                  </th>
                  <th className="px-4 py-3 text-center font-semibold">
                    Total Spending Returning Cust
                  </th>
                  <th className="px-4 py-3 text-center font-bold">
                    TOTAL SALES
                  </th>
                </tr>
              </thead>
              <motion.tbody
                className="divide-y divide-gray-200"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {bc.map((employee, index) => (
                  <tr
                    key={`${employee.id} - ${index}`}
                    className={`${
                      index % 2 === 0 ? "bg-white" : "bg-gray-50"
                    } hover:bg-gray-100 transition`}
                  >
                    {/* Sticky Employee Name Column */}
                    <td
                      className={`sticky left-0 ${
                        index % 2 === 0 ? "bg-white" : "bg-gray-50"
                      } z-10 px-6 py-4 whitespace-nowrap border-r border-gray-200 shadow-md`}
                    >
                      <div className="row-start-center gap-2 w-[12rem]">
                        <Image
                          preview
                          src={employee.image}
                          alt={employee.name}
                          onError={(e) => {
                            e.target.src = "/logo192.png";
                          }}
                          pt={{
                            image: {
                              className:
                                "w-8 h-8 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-gray-200 flex-shrink-0",
                            },
                          }}
                        />
                        <div className="font-semibold text-gray-900 truncate max-w-[70%]">
                          {employee.name}
                        </div>
                      </div>
                    </td>

                    {/* Pax Cells */}
                    {[
                      employee.totalAppointments,
                      employee.returningCustomer,
                      employee.newCustomer,
                      employee.newPayingCustomer,
                    ].map((val, i) => (
                      <td
                        key={i}
                        className="px-4 py-4 text-center whitespace-nowrap"
                      >
                        <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-black bg-gray-200 rounded">
                          {formatPax(val)}
                        </span>
                      </td>
                    ))}

                    {/* Conversion Rate */}
                    <td className="px-4 py-4 text-center whitespace-nowrap font-bold">
                      <div
                        className={`inline-flex items-center gap-1 px-4 py-1 text-sm font-bold rounded border ${getConversionColor(
                          employee.conversionRate
                        )}`}
                        title={
                          employee.conversionRate >= 70
                            ? "Excellent Conversion"
                            : employee.conversionRate >= 40
                            ? "Average Conversion"
                            : "Needs Improvement"
                        }
                      >
                        <span className="mr-1">
                          {getConversionIcon(employee.conversionRate)}
                        </span>

                        {formatPercentage(employee.conversionRate)}
                      </div>
                    </td>
                    {/* Global Conversion Rate */}
                    <td className="px-4 py-4 text-center font-medium whitespace-nowrap">
                      <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-black bg-gray-200 rounded">
                        {getGlobalConvRate(employee)}%
                      </span>
                    </td>
                    {/* Financial Data */}
                    <td className="px-4 py-4 text-right font-medium whitespace-nowrap">
                      {formatCurrency(employee.totalSpendingNew)}
                    </td>
                    <td className="px-4 py-4 text-right font-medium whitespace-nowrap">
                      {formatCurrency(employee.avgSpendingPerNew)}
                    </td>
                    <td className="px-4 py-4 text-right font-medium whitespace-nowrap">
                      {formatCurrency(employee.totalSpendingReturning)}
                    </td>

                    {/* Total Sales Highlight */}
                    <td className="px-4 py-4 text-center whitespace-nowrap">
                      <div
                        className={`inline-block px-4 py-1 text-sm font-bold rounded border ${getSalesColor(
                          employee.totalSales,
                          25000000
                        )}`}
                      >
                        {formatCurrency(employee.totalSales)}
                      </div>
                    </td>
                  </tr>
                ))}
              </motion.tbody>
            </table>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OwnerDashboardEmployeeBCDetail;
